import "@patron/patron-css/patron/index.css";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// Patron UI components
import { Label } from "@patron/patron-react/label";
import { TextInput } from "@patron/patron-react/textinput";
import { Checkbox } from "@patron/patron-react/checkbox";
import { Button } from "@patron/patron-react/button";

// External packages
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Constants and utilities
import * as serverConstants from "../../constants/ServerConstants";
import * as constants from "../../constants/Constants";
import * as EmailUtil from "../../util/EmailUtility";

// Type definitions
import { EnterpriseDetailsPropType } from "./propTypes";

// Styles
import "./EnterpriseRegistration.scss";

/**
 * Shared form component for both EMM and Customer Managed registration
 */
const EnterpriseRegistrationForm = ({
  enterpriseResponse,
  initialFormData,
  registrationType,
  showSpinner,
  showOrHideButton,
  enterpriseToken,
  signupUrlName,
  onFormDataChange,
  onFormValidityChange,
  onSubmit,
  showTermsCheckbox = true,
}) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    enterpriseDisplayName: "",
    dataProtectionOfficerName: "",
    dataProtectionOfficerEmail: "",
    dataProtectionOfficerPhone: "",
    euRepresentativeName: "",
    euRepresentativeEmail: "",
    euRepresentativePhone: "",
    contactEmail: "",
    termsAndConditions: false,
    validDataProtectionOfficerEmail: false,
    validEuRepresentativeEmail: false,
    validContactEmail: false,
    validDataProtectionOfficerPhone: false,
    validEuRepresentativePhone: false,
    enterprise_token: enterpriseToken || "",
    enterprise_name:
      registrationType === constants.CUSTOMER_MANAGED_TYPE
        ? constants.CUSTOMER_MANAGED_TYPE
        : "",
    signupurl_name: signupUrlName || "",
  });

  useEffect(() => {
    // Initialize form data from props if provided
    if (initialFormData) {
      setFormState((prevState) => ({
        ...prevState,
        ...initialFormData,
      }));
    }

    // Set enterprise token if provided
    if (enterpriseToken) {
      setFormState((prevState) => ({
        ...prevState,
        enterprise_token: enterpriseToken,
        termsAndConditions: true,
      }));
    }
  }, [initialFormData, enterpriseToken]);

  // Handle form field changes
  const handleInputChange = (
    field,
    value,
    validationField = null,
    validationFn = null
  ) => {
    const updatedState = { ...formState };

    // Reset validation state if applicable
    if (validationField) {
      updatedState[validationField] = false;
    }

    // Perform validation if needed
    if (validationFn !== null && validationFn(value)) {
      updatedState[validationField] = true;
      if (onFormValidityChange) {
        onFormValidityChange(validationField, true);
      }
    } else {
      updatedState[field] = value;
      if (onFormDataChange) {
        onFormDataChange(field, value);
      }
      if (validationField && onFormValidityChange) {
        onFormValidityChange(validationField, false);
      }
    }
    setFormState(updatedState);
  };

  // Toggle terms and conditions
  const toggleTermsAndConditions = () => {
    const newValue = !formState.termsAndConditions;
    setFormState({ ...formState, termsAndConditions: newValue });
    if (onFormDataChange) {
      onFormDataChange("termsAndConditions", newValue);
    }
  };

  // Phone number validation helper
  const phoneNumberValidation = (phoneNumber) => {
    return (
      phoneNumber !== "" && !new RegExp(/^[0-9\b+\-()]+/).test(phoneNumber)
    );
  };

  // Function to handle form submission
  const handleSubmit = () => {
    // Add customer managed specific fields if needed
    if (registrationType === constants.CUSTOMER_MANAGED_TYPE) {
      const submissionData = {
        ...formState,
        enterprise_token: enterpriseToken,
        enterprise_name: constants.CUSTOMER_MANAGED_TYPE,
        signupurl_name: signupUrlName,
      };

      if (onSubmit) {
        onSubmit(
          enterpriseResponse.enterpriseId
            ? constants.HTTP_PATCH
            : constants.HTTP_POST,
          submissionData
        );
      }
    } else if (registrationType === constants.ENTERPRISE_MANAGED_TYPE) {
      if (onSubmit) {
        onSubmit(
          enterpriseResponse.enterpriseId
            ? constants.HTTP_PATCH
            : constants.HTTP_POST,
          formState
        );
      }
    } else {
      console.error(`Unknown registration type: ${registrationType}`);
    }
  };

  // Check if the form is valid for submission
  const isFormValid = () => {
    if (
      registrationType === constants.CUSTOMER_MANAGED_TYPE &&
      enterpriseToken
    ) {
      // For customer managed with token, form is always valid
      return true;
    }

    const baseValidation =
      !showSpinner &&
      (registrationType === constants.CUSTOMER_MANAGED_TYPE ||
        formState.termsAndConditions) &&
      !formState.validDataProtectionOfficerEmail &&
      !formState.validEuRepresentativeEmail &&
      !formState.validContactEmail &&
      !formState.validDataProtectionOfficerPhone &&
      !formState.validEuRepresentativePhone;

    // For EMM type, business name is required
    if (registrationType !== constants.CUSTOMER_MANAGED_TYPE) {
      return baseValidation && formState.enterpriseDisplayName !== "";
    }

    // For Customer Managed, business name is not required
    return baseValidation;
  };

  return (
    <div>
      {enterpriseResponse && enterpriseResponse.enterpriseId !== "" && (
        <section className="mt-5 p-5 inline-edit-form">
          <form>
            <div className="form-group">
              <div className="left-section">
                <label className="left-label">{t("enterpriseId")}: </label>
              </div>
              <div className="right-section">
                <label className="right-label">
                  {enterpriseResponse.enterpriseId}
                </label>
              </div>
            </div>
            <div className="form-group">
              <div className="left-section">
                <label className="left-label">{t("type")}: </label>
              </div>
              <div className="right-section">
                <label className="right-label">
                  {enterpriseResponse.accountType}
                </label>
              </div>
            </div>
            <div className="form-group">
              <div className="left-section">
                <label className="left-label">{t("serviceAccount")}: </label>
              </div>
              <div className="right-section">
                <label className="right-label">
                  {enterpriseResponse.serviceAccount}
                </label>
              </div>
            </div>
          </form>
        </section>
      )}

      {(enterpriseResponse?.enterpriseId === "" ||
        !enterpriseResponse ||
        (enterpriseResponse.enterpriseId !== "" &&
          (enterpriseResponse.accountType ===
            constants.EMM_MANAGED_ENTERPRISE_TYPE ||
            enterpriseResponse.accountType ===
              constants.CME_MANAGED_ENTERPRISE_TYPE))) && (
        <div>
          <div id="displayNameContactInfo">
            <section className="mt-2 parameters-section">
              <div className="hcl-row">
                <div className="hcl-col-6">
                  <div className="hcl-row">
                    <div className="mt-2 ml-2 hcl-col-8 hcl-form-group">
                      <TextInput
                        type="text"
                        id="businessName"
                        maxLength={100}
                        value={formState.enterpriseDisplayName}
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          handleInputChange("enterpriseDisplayName", value);
                        }}
                        required={
                          registrationType !== constants.CUSTOMER_MANAGED_TYPE
                        } // Not required for Customer Managed
                      />
                      <Label htmlFor="businessName">
                        {t("businessName")}
                        {registrationType !==
                          constants.CUSTOMER_MANAGED_TYPE && (
                          <span className="span-style">*</span>
                        )}
                      </Label>
                    </div>
                  </div>
                </div>
                {registrationType !== constants.CUSTOMER_MANAGED_TYPE && (
                  <div className="hcl-col-6">
                    <div className="hcl-row">
                      <div className="mt-2 ml-2 hcl-col-8 hcl-form-group">
                        <TextInput
                          id="contactInfoEmail"
                          aria-label="text input"
                          data-invalid={formState.validContactEmail}
                          value={formState.contactEmail}
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            handleInputChange(
                              "contactEmail",
                              value,
                              "validContactEmail",
                              EmailUtil.EmailAddressValidation
                            );
                          }}
                          required
                        />
                        <Label htmlFor="contactInfoEmail">
                          {t("contactEmail")}
                        </Label>
                        {formState.validContactEmail && (
                          <div className="hcl-error-msg">
                            {t("notAValidEmailAddress")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
          {registrationType !== constants.CUSTOMER_MANAGED_TYPE && (
            <div id="dataAndEU">
              <section className="mt-2 parameters-section">
                <div className="hcl-row">
                  <div className="hcl-col-6">
                    <Label className="mt-2 ml-1">
                      {t("dataProtectionOfficer")}
                    </Label>
                    <div className="hcl-row">
                      <div className="mt-2 ml-2 hcl-col-8 hcl-form-group">
                        <TextInput
                          type="text"
                          id="dataProtectionOfficerName"
                          value={formState.dataProtectionOfficerName}
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            handleInputChange(
                              "dataProtectionOfficerName",
                              value
                            );
                          }}
                          required
                        />
                        <Label htmlFor="dataProtectionOfficerName">
                          {t("name")}
                        </Label>
                      </div>
                    </div>
                    <div className="hcl-row">
                      <div className="ml-2 hcl-col-8 hcl-form-group">
                        <TextInput
                          id="dataProtectionOfficerEmail"
                          aria-label="text input"
                          data-invalid={
                            formState.validDataProtectionOfficerEmail
                          }
                          value={formState.dataProtectionOfficerEmail}
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            handleInputChange(
                              "dataProtectionOfficerEmail",
                              value,
                              "validDataProtectionOfficerEmail",
                              EmailUtil.EmailAddressValidation
                            );
                          }}
                          required
                        />
                        <Label htmlFor="dataProtectionOfficerEmail">
                          {t("email")}
                        </Label>
                        {formState.validDataProtectionOfficerEmail && (
                          <div className="hcl-error-msg">
                            {t("notAValidEmailAddress")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="hcl-row">
                      <div className="ml-2 hcl-col-8 hcl-form-group">
                        <TextInput
                          id="dataProtectionOfficerPhone"
                          aria-label="text input"
                          data-invalid={
                            formState.validDataProtectionOfficerPhone
                          }
                          value={formState.dataProtectionOfficerPhone}
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            handleInputChange(
                              "dataProtectionOfficerPhone",
                              value,
                              "validDataProtectionOfficerPhone",
                              phoneNumberValidation
                            );
                          }}
                          required
                        />
                        <Label htmlFor="dataProtectionOfficerPhone">
                          {t("phone")}
                        </Label>
                        {formState.validDataProtectionOfficerPhone && (
                          <div className="hcl-error-msg">
                            {t("notAValidPhoneNumber")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="hcl-col-6">
                    <Label className="mt-2 ml-1">{t("euRepresentative")}</Label>
                    <div className="hcl-row">
                      <div className="mt-2 ml-2 hcl-col-8 hcl-form-group">
                        <TextInput
                          type="text"
                          id="euRepresentativeName"
                          value={formState.euRepresentativeName}
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            handleInputChange("euRepresentativeName", value);
                          }}
                          required
                        />
                        <Label htmlFor="euRepresentativeName">
                          {t("name")}
                        </Label>
                      </div>
                    </div>
                    <div className="hcl-row">
                      <div className="ml-2 hcl-col-8 hcl-form-group">
                        <TextInput
                          id="euRepresentativeEmail"
                          aria-label="text input"
                          data-invalid={formState.validEuRepresentativeEmail}
                          value={formState.euRepresentativeEmail}
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            handleInputChange(
                              "euRepresentativeEmail",
                              value,
                              "validEuRepresentativeEmail",
                              EmailUtil.EmailAddressValidation
                            );
                          }}
                          required
                        />
                        <Label htmlFor="euRepresentativeEmail">
                          {t("email")}
                        </Label>
                        {formState.validEuRepresentativeEmail && (
                          <div className="hcl-error-msg">
                            {t("notAValidEmailAddress")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="hcl-row">
                      <div className="ml-2 hcl-col-8 hcl-form-group">
                        <TextInput
                          id="euRepresentativePhone"
                          aria-label="text input"
                          data-invalid={formState.validEuRepresentativePhone}
                          value={formState.euRepresentativePhone}
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            handleInputChange(
                              "euRepresentativePhone",
                              value,
                              "validEuRepresentativePhone",
                              phoneNumberValidation
                            );
                          }}
                          required
                        />
                        <Label htmlFor="euRepresentativePhone">
                          {t("phone")}
                        </Label>
                        {formState.validEuRepresentativePhone && (
                          <div className="hcl-error-msg">
                            {t("notAValidPhoneNumber")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* Terms and Conditions Checkbox - Only shown for EMM or when specified */}
          {showTermsCheckbox && (
            <div id="termsAndConditions">
              <div className="hcl-row">
                <div className="mt-2 ml-3">
                  <Checkbox
                    id="termsConditions"
                    label={t("termsAndConditions")}
                    checked={formState.termsAndConditions}
                    onChange={toggleTermsAndConditions}
                  />
                </div>{" "}
                &nbsp;
                <div className="mt-3 hcl-form-group">
                  <Link
                    to={{
                      pathname: serverConstants.ANDROID_TERMS_AND_CONDITIONS,
                    }}
                    target="_blank"
                    title={t("managedGooglePlayAgreement")}
                    rel="noopener noreferrer"
                  >
                    {t("managedGooglePlayAgreement")}
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Register/Update button */}
      <div className="register-button">
        <Button
          type="primary"
          disabled={showSpinner || showOrHideButton || !isFormValid()}
          onClick={handleSubmit}
        >
          {enterpriseResponse?.enterpriseId ? t("update") : t("register")}
        </Button>
      </div>
    </div>
  );
};

EnterpriseRegistrationForm.propTypes = {
  /**
   * The enterprise response data from the API
   * @see EnterpriseResponse in models.js
   */
  enterpriseResponse: PropTypes.shape({
    enterpriseId: PropTypes.string,
    enterpriseDisplayName: PropTypes.string,
    accountType: PropTypes.string,
    serviceAccount: PropTypes.string,
    hasToken: PropTypes.bool,
    enterpriseDetails: PropTypes.oneOfType([
      PropTypes.string,
      EnterpriseDetailsPropType,
    ]),
  }),
  initialFormData: PropTypes.object,
  registrationType: PropTypes.oneOf([
    constants.ENTERPRISE_MANAGED_TYPE,
    constants.CUSTOMER_MANAGED_TYPE,
  ]).isRequired,
  showSpinner: PropTypes.bool,
  showOrHideButton: PropTypes.bool,
  enterpriseToken: PropTypes.string,
  signupUrlName: PropTypes.string,
  onFormDataChange: PropTypes.func,
  onFormValidityChange: PropTypes.func,
  onSubmit: PropTypes.func,
  showTermsCheckbox: PropTypes.bool,
};

export default EnterpriseRegistrationForm;
